/**
 * Biblioteca de modelos de projeto: o TEXTO que o MeuGantt semeia no primeiro
 * projeto do usuário.
 *
 * Este namespace é diferente dos outros: o que sai daqui não fica na tela, vira
 * dado do cliente no banco no instante em que o projeto é criado (nome do
 * projeto, membros da equipe, fases e tarefas). Não dá para retraduzir depois
 * sem mexer em dado de gente, então o idioma tem que estar certo NA CRIAÇÃO.
 * Por isso `src/data/projectTemplates.ts` lê daqui via `getCurrentCopy()`, e
 * não por `useCopy()`: quem consome é serviço, não componente.
 *
 * A ESTRUTURA (duração, tipo, dependência, cor, ordem das fases) fica no
 * código; aqui só existe texto.
 *
 * As chaves de `projects` são as chaves dos modelos, passadas pelo
 * `WelcomeModal` e pelo `AIProjectModal` — nunca mudam com o idioma. Membro,
 * fase e tarefa também são casados por CHAVE NOMEADA com a estrutura, nunca por
 * índice de array: um idioma com uma tarefa a menos compilaria e sumiria com a
 * tarefa em silêncio.
 *
 * `team[].roleTitle` é o que vira `role_title` no banco E o que o
 * `assigned_role` de cada tarefa aponta (a tarefa referencia a CHAVE do membro,
 * e o `assigned_role` sai do `roleTitle` dele). É assim que o casamento de
 * `aiProjectService.roleKey()` sobrevive à tradução: os dois lados nascem da
 * mesma string, no mesmo idioma.
 */
const templates = {
  /**
   * Palavras-chave do matching de `findBestTemplate` sobre o texto livre do
   * usuário. Uma lista por idioma, porque a pessoa descreve o projeto no idioma
   * em que está usando o produto ("mobile app launch" não casava com nada e
   * caía no default).
   *
   * Regras: tudo em minúsculo (a pontuação só normaliza acento, não caixa) e a
   * ORDEM das chaves é a mesma nos três idiomas — empate de pontuação é
   * resolvido pela primeira chave visitada.
   */
  keywords: {
    website: ['website', 'site', 'web', 'landing page', 'e-commerce', 'ecommerce', 'loja virtual', 'loja online', 'portal', 'blog', 'página', 'pagina'],
    software: ['software', 'aplicativo', 'app', 'sistema', 'plataforma', 'saas', 'api', 'mobile', 'desktop', 'programa', 'ferramenta digital'],
    marketing: ['marketing', 'campanha', 'redes sociais', 'social media', 'publicidade', 'anúncio', 'anuncio', 'branding', 'marca', 'comunicação', 'conteúdo', 'conteudo', 'digital', 'seo', 'ads'],
    evento: ['evento', 'conferência', 'conferencia', 'workshop', 'seminário', 'seminario', 'meetup', 'encontro', 'congresso', 'feira', 'lançamento', 'lancamento', 'festa', 'celebração'],
    implantacao: ['implantação', 'implantacao', 'erp', 'crm', 'sap', 'totvs', 'migração de sistema', 'migracao', 'troca de sistema', 'implementação', 'implementacao'],
  },

  projects: {
    software: {
      name: 'Desenvolvimento de Software',
      description: 'Projeto de desenvolvimento de software seguindo metodologia ágil com fases bem definidas de descoberta, design, implementação, testes e lançamento.',
      team: {
        pm: { name: 'Gerente de Projeto', roleTitle: 'Gerente de Projeto' },
        po: { name: 'Product Owner', roleTitle: 'Product Owner' },
        ux: { name: 'UX Designer', roleTitle: 'UX Designer' },
        frontend: { name: 'Dev Frontend', roleTitle: 'Desenvolvedor Frontend' },
        backend: { name: 'Dev Backend', roleTitle: 'Desenvolvedor Backend' },
        qa: { name: 'QA Analyst', roleTitle: 'Analista de Qualidade' },
      },
      phases: {
        discovery: {
          name: 'Fase 1: Descoberta e Planejamento',
          tasks: {
            requirements: { name: 'Levantamento de requisitos', description: 'Entrevistas com stakeholders e documentação de requisitos funcionais e não-funcionais' },
            feasibility: { name: 'Análise de viabilidade técnica', description: 'Avaliar tecnologias, integrações e restrições técnicas' },
            scope: { name: 'Definição do escopo e backlog', description: 'Priorizar funcionalidades e criar backlog inicial' },
            schedule: { name: 'Cronograma e estimativas', description: 'Definir timeline, marcos e alocação de recursos' },
            kickoff: { name: 'Kick-off aprovado' },
          },
        },
        design: {
          name: 'Fase 2: Design UX/UI',
          tasks: {
            userResearch: { name: 'Pesquisa com usuários', description: 'Pesquisa qualitativa e quantitativa com público-alvo' },
            wireframes: { name: 'Wireframes e fluxos', description: 'Criar wireframes de baixa fidelidade e mapas de fluxo' },
            visualDesign: { name: 'Design visual e protótipo', description: 'Interface visual de alta fidelidade e protótipo interativo' },
            validation: { name: 'Validação com stakeholders', description: 'Apresentar protótipo e coletar feedback' },
            approved: { name: 'Design aprovado' },
          },
        },
        development: {
          name: 'Fase 3: Desenvolvimento',
          tasks: {
            setup: { name: 'Setup do ambiente e arquitetura', description: 'Configurar repositório, CI/CD, ambientes e arquitetura base' },
            frontend: { name: 'Desenvolvimento Frontend', description: 'Implementar componentes, páginas e integrações visuais' },
            backend: { name: 'Desenvolvimento Backend e API', description: 'Implementar APIs, regras de negócio e banco de dados' },
            integration: { name: 'Integração Frontend + Backend', description: 'Conectar interface com APIs e testar fluxos integrados' },
            done: { name: 'Desenvolvimento concluído' },
          },
        },
        testing: {
          name: 'Fase 4: Testes e Qualidade',
          tasks: {
            automatedTests: { name: 'Testes unitários e integração', description: 'Executar testes automatizados e revisar cobertura' },
            uat: { name: 'Testes de aceitação (UAT)', description: 'Validar funcionalidades com critérios de aceite definidos' },
            bugfix: { name: 'Correção de bugs', description: 'Corrigir defeitos encontrados nos testes' },
            approved: { name: 'Testes aprovados' },
          },
        },
        launch: {
          name: 'Fase 5: Lançamento',
          tasks: {
            prodEnv: { name: 'Preparação do ambiente de produção', description: 'Configurar servidores, DNS, SSL e monitoramento' },
            dataMigration: { name: 'Migração de dados', description: 'Migrar dados do ambiente anterior se aplicável' },
            deploy: { name: 'Deploy em produção', description: 'Realizar deploy e smoke tests' },
            training: { name: 'Treinamento dos usuários', description: 'Capacitar usuários finais e documentar' },
            goLive: { name: 'Go-Live' },
          },
        },
      },
    },

    website: {
      name: 'Desenvolvimento de Website',
      description: 'Projeto de criação de website profissional com design responsivo, otimização SEO e integração com ferramentas de analytics.',
      team: {
        pm: { name: 'Gerente de Projeto', roleTitle: 'Gerente de Projeto' },
        designer: { name: 'Web Designer', roleTitle: 'Web Designer' },
        developer: { name: 'Desenvolvedor Web', roleTitle: 'Desenvolvedor Web' },
        writer: { name: 'Redator', roleTitle: 'Redator de Conteúdo' },
        seo: { name: 'Especialista SEO', roleTitle: 'Especialista SEO' },
      },
      phases: {
        planning: {
          name: 'Fase 1: Planejamento',
          tasks: {
            briefing: { name: 'Briefing e objetivos do site', description: 'Definir público-alvo, objetivos de negócio e funcionalidades principais' },
            competitors: { name: 'Análise de concorrentes', description: 'Mapear sites concorrentes e identificar oportunidades' },
            sitemap: { name: 'Mapa do site e arquitetura', description: 'Definir estrutura de páginas e navegação' },
            approved: { name: 'Planejamento aprovado' },
          },
        },
        design: {
          name: 'Fase 2: Design',
          tasks: {
            wireframes: { name: 'Wireframes das páginas', description: 'Layout esquemático de todas as páginas do site' },
            visualDesign: { name: 'Design visual (desktop e mobile)', description: 'Design responsivo de alta fidelidade' },
            approval: { name: 'Aprovação do design', description: 'Revisão e aprovação com cliente' },
            done: { name: 'Design finalizado' },
          },
        },
        content: {
          name: 'Fase 3: Conteúdo',
          tasks: {
            copywriting: { name: 'Redação de textos', description: 'Criar conteúdo para todas as páginas do site' },
            keywords: { name: 'Pesquisa de palavras-chave', description: 'Definir keywords para otimização de cada página' },
            media: { name: 'Produção de imagens e mídia', description: 'Criar ou selecionar imagens, ícones e vídeos' },
            ready: { name: 'Conteúdo pronto' },
          },
        },
        development: {
          name: 'Fase 4: Desenvolvimento',
          tasks: {
            coding: { name: 'Desenvolvimento HTML/CSS/JS', description: 'Codificar páginas com design responsivo' },
            contentIntegration: { name: 'Integração de conteúdo', description: 'Inserir textos, imagens e configurar formulários' },
            seoOnPage: { name: 'Otimização SEO on-page', description: 'Meta tags, schema markup, sitemap e robots.txt' },
            analytics: { name: 'Configuração de analytics', description: 'Google Analytics, Search Console e tags de conversão' },
            built: { name: 'Site desenvolvido' },
          },
        },
        launch: {
          name: 'Fase 5: Lançamento',
          tasks: {
            crossBrowser: { name: 'Testes cross-browser e mobile', description: 'Testar em diferentes navegadores e dispositivos' },
            hosting: { name: 'Configuração de hospedagem', description: 'DNS, SSL, CDN e configuração do servidor' },
            publish: { name: 'Publicação do site', description: 'Deploy final e verificação de funcionamento' },
            live: { name: 'Site no ar' },
          },
        },
      },
    },

    marketing: {
      name: 'Campanha de Marketing Digital',
      description: 'Planejamento e execução de campanha de marketing digital integrada, incluindo redes sociais, email marketing, mídia paga e produção de conteúdo.',
      team: {
        coordinator: { name: 'Coordenador de Marketing', roleTitle: 'Coordenador de Marketing' },
        socialMedia: { name: 'Social Media', roleTitle: 'Analista de Redes Sociais' },
        designer: { name: 'Designer Gráfico', roleTitle: 'Designer Gráfico' },
        copywriter: { name: 'Copywriter', roleTitle: 'Copywriter' },
        mediaAnalyst: { name: 'Analista de Mídia', roleTitle: 'Analista de Mídia Paga' },
      },
      phases: {
        strategy: {
          name: 'Fase 1: Estratégia',
          tasks: {
            goals: { name: 'Definição de objetivos e KPIs', description: 'Estabelecer metas SMART e indicadores de performance' },
            audience: { name: 'Análise de público-alvo e personas', description: 'Mapear perfil demográfico, comportamental e interesses' },
            channels: { name: 'Planejamento de canais e orçamento', description: 'Definir mix de canais e distribuição de verba' },
            approved: { name: 'Estratégia aprovada' },
          },
        },
        content: {
          name: 'Fase 2: Produção de Conteúdo',
          tasks: {
            calendar: { name: 'Calendário editorial', description: 'Planejar publicações e temas para o período da campanha' },
            copy: { name: 'Redação de copies e textos', description: 'Criar textos para posts, emails e anúncios' },
            visuals: { name: 'Criação de peças visuais', description: 'Design de banners, posts, stories e materiais visuais' },
            review: { name: 'Revisão e aprovação de conteúdo', description: 'Revisar todos os materiais produzidos' },
            approved: { name: 'Conteúdo aprovado' },
          },
        },
        setup: {
          name: 'Fase 3: Configuração e Lançamento',
          tasks: {
            paidMedia: { name: 'Configuração de campanhas de mídia paga', description: 'Configurar segmentação, lances e criativos nas plataformas' },
            email: { name: 'Setup de email marketing', description: 'Criar templates, listas e automações de email' },
            tracking: { name: 'Configuração de tracking e pixels', description: 'Instalar pixels de conversão e UTMs' },
            launch: { name: 'Lançamento da campanha', description: 'Ativar todas as ações simultaneamente' },
            live: { name: 'Campanha no ar' },
          },
        },
        monitoring: {
          name: 'Fase 4: Monitoramento e Otimização',
          tasks: {
            dailyMetrics: { name: 'Monitoramento diário de métricas', description: 'Acompanhar performance de campanhas e ajustar lances' },
            socialManagement: { name: 'Gestão de redes sociais', description: 'Publicar conteúdo e interagir com audiência' },
            optimization: { name: 'Otimização de campanhas', description: 'Ajustar segmentação e criativos com base nos dados' },
            finalReport: { name: 'Relatório final e aprendizados', description: 'Compilar resultados, ROI e recomendações futuras' },
            closed: { name: 'Campanha finalizada' },
          },
        },
      },
    },

    evento: {
      name: 'Organização de Evento Corporativo',
      description: 'Planejamento e execução de evento corporativo incluindo logística, comunicação, fornecedores e gestão no dia do evento.',
      team: {
        coordinator: { name: 'Coordenador do Evento', roleTitle: 'Coordenador de Eventos' },
        communications: { name: 'Analista de Comunicação', roleTitle: 'Analista de Comunicação' },
        logistics: { name: 'Assistente de Logística', roleTitle: 'Assistente de Logística' },
        designer: { name: 'Designer', roleTitle: 'Designer Gráfico' },
      },
      phases: {
        planning: {
          name: 'Fase 1: Planejamento',
          tasks: {
            concept: { name: 'Definição de conceito e formato', description: 'Tipo de evento, tema, público esperado e objetivos' },
            budget: { name: 'Definição de orçamento', description: 'Levantar custos estimados e aprovar verba' },
            venue: { name: 'Seleção e reserva do local', description: 'Pesquisar, visitar e contratar espaço para o evento' },
            agenda: { name: 'Cronograma do evento', description: 'Programação detalhada com horários e atividades' },
            approved: { name: 'Planejamento aprovado' },
          },
        },
        vendors: {
          name: 'Fase 2: Fornecedores e Logística',
          tasks: {
            hiring: { name: 'Contratação de fornecedores', description: 'Buffet, audiovisual, decoração e demais serviços' },
            speakers: { name: 'Contratação de palestrantes/atrações', description: 'Convite, contrato e briefing com apresentadores' },
            materials: { name: 'Logística de materiais', description: 'Crachás, brindes, materiais impressos e sinalização' },
            confirmed: { name: 'Fornecedores confirmados' },
          },
        },
        communication: {
          name: 'Fase 3: Comunicação e Inscrições',
          tasks: {
            branding: { name: 'Criação de identidade visual', description: 'Logo do evento, paleta de cores e materiais gráficos' },
            landingPage: { name: 'Página de inscrição / landing page', description: 'Criar página com informações e formulário de inscrição' },
            promotion: { name: 'Divulgação e convites', description: 'Email marketing, redes sociais e convites personalizados' },
            registrations: { name: 'Gestão de inscrições', description: 'Confirmar presenças e gerenciar lista de participantes' },
            closed: { name: 'Inscrições encerradas' },
          },
        },
        execution: {
          name: 'Fase 4: Execução e Pós-Evento',
          tasks: {
            setup: { name: 'Montagem do espaço', description: 'Preparar local, decoração, equipamentos e sinalização' },
            eventDay: { name: 'Dia do evento', description: 'Coordenação geral, recepção e gestão de imprevistos' },
            teardown: { name: 'Desmontagem', description: 'Devolução de equipamentos e limpeza do espaço' },
            survey: { name: 'Pesquisa de satisfação', description: 'Enviar pesquisa aos participantes e tabular resultados' },
            finalReport: { name: 'Relatório final', description: 'Compilar métricas, feedback e lições aprendidas' },
            done: { name: 'Evento concluído' },
          },
        },
      },
    },

    implantacao: {
      name: 'Implantação de Sistema',
      description: 'Projeto de implantação de sistema corporativo incluindo análise de processos, configuração, migração de dados, treinamento e go-live.',
      team: {
        pm: { name: 'Gerente de Projeto', roleTitle: 'Gerente de Projeto' },
        functional: { name: 'Consultor Funcional', roleTitle: 'Consultor Funcional' },
        technical: { name: 'Consultor Técnico', roleTitle: 'Consultor Técnico' },
        dataAnalyst: { name: 'Analista de Dados', roleTitle: 'Analista de Dados' },
        keyUser: { name: 'Key User', roleTitle: 'Key User / Multiplicador' },
      },
      phases: {
        analysis: {
          name: 'Fase 1: Análise e Mapeamento',
          tasks: {
            asIs: { name: 'Mapeamento de processos atuais (AS-IS)', description: 'Documentar processos de negócio atuais da organização' },
            requirements: { name: 'Levantamento de requisitos', description: 'Identificar necessidades, gaps e customizações necessárias' },
            toBe: { name: 'Desenho de processos futuros (TO-BE)', description: 'Projetar novos processos otimizados com o sistema' },
            approved: { name: 'Análise de gaps aprovada' },
          },
        },
        configuration: {
          name: 'Fase 2: Configuração e Customização',
          tasks: {
            install: { name: 'Instalação e configuração base', description: 'Instalar sistema e configurar parâmetros iniciais' },
            parameterization: { name: 'Parametrização funcional', description: 'Configurar módulos conforme processos TO-BE definidos' },
            customizations: { name: 'Desenvolvimento de customizações', description: 'Implementar ajustes, relatórios e integrações específicas' },
            permissions: { name: 'Configuração de perfis e permissões', description: 'Definir perfis de acesso e políticas de segurança' },
            configured: { name: 'Sistema configurado' },
          },
        },
        migration: {
          name: 'Fase 3: Migração de Dados',
          tasks: {
            legacyMapping: { name: 'Mapeamento de dados legados', description: 'Identificar dados a migrar e mapear campos de/para' },
            cleanup: { name: 'Limpeza e preparação dos dados', description: 'Tratar inconsistências, duplicatas e padronizar formatos' },
            load: { name: 'Carga de dados no novo sistema', description: 'Executar scripts de migração e importação' },
            validation: { name: 'Validação dos dados migrados', description: 'Conferir integridade e consistência dos dados' },
            validated: { name: 'Migração validada' },
          },
        },
        training: {
          name: 'Fase 4: Testes e Treinamento',
          tasks: {
            integratedTests: { name: 'Testes integrados', description: 'Validar fluxos completos de ponta a ponta' },
            fixes: { name: 'Correção de ajustes', description: 'Corrigir problemas identificados nos testes' },
            material: { name: 'Preparação do material de treinamento', description: 'Criar manuais, vídeos e material de apoio' },
            keyUsers: { name: 'Treinamento dos key users', description: 'Capacitar multiplicadores que treinarão os demais' },
            endUsers: { name: 'Treinamento dos usuários finais', description: 'Key users treinam suas equipes' },
            approved: { name: 'Homologação concluída' },
          },
        },
        goLive: {
          name: 'Fase 5: Go-Live e Estabilização',
          tasks: {
            preparation: { name: 'Preparação do go-live', description: 'Checklist final, plano de contingência e comunicação' },
            cutover: { name: 'Go-live (virada)', description: 'Ativar sistema em produção e desativar legado' },
            hypercare: { name: 'Suporte pós go-live', description: 'Acompanhamento intensivo e resolução de dúvidas' },
            report: { name: 'Relatório de estabilização', description: 'Documentar status, pendências e plano de evolução' },
            closed: { name: 'Projeto encerrado' },
          },
        },
      },
    },
  },
};

export type templatesCopy = typeof templates;

export default templates;
